import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FolderOpen, TreeStructure, CloudArrowUp, ArrowsClockwise } from "@phosphor-icons/react";
import { FmnWatermark } from "../FmnWatermark";

const FADE = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } };

const STEP_ICONS = [FolderOpen, TreeStructure, CloudArrowUp, ArrowsClockwise];

export function HowItWorks() {
    const { t } = useTranslation();
    const steps = t("how_it_works.steps", { returnObjects: true });
    const stepList = Array.isArray(steps) ? steps : [];

    return (
        <section className="relative py-32 px-6 bg-white overflow-hidden" data-testid="how-section" id="how">
            <FmnWatermark position="right" size={800} opacity={0.05} rotate={-10} />
            <FmnWatermark position="bottom-left" size={600} opacity={0.05} rotate={14} />
            <div className="relative max-w-7xl mx-auto">
                <motion.div {...FADE} className="text-center mb-20">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-6">
                        {t("how_it_works.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight max-w-3xl mx-auto">
                        {t("how_it_works.headline_part1")} <em className="italic text-[#4A5A95]">{t("how_it_works.headline_emphasis")}</em>
                    </h2>
                    <p className="mt-6 text-base text-[#5C5A59] font-light italic max-w-2xl mx-auto leading-relaxed">
                        {t("how_it_works.sub")}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {stepList.map((step, i) => {
                        const Icon = STEP_ICONS[i] || FolderOpen;
                        const isHighlight = i === 3;
                        return (
                            <motion.div
                                key={step.n}
                                initial={{ opacity: 0, y: 28 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                                className="relative"
                                data-testid={`how-step-${i + 1}`}
                            >
                                <div className={`rounded-3xl p-10 h-full transition-all hover:-translate-y-1 hover:shadow-rem-card ${
                                    isHighlight
                                        ? "bg-gradient-to-br from-[#EDF0F8] via-[#F4F6FB] to-white border border-[#8C9ECB]/40 shadow-rem-soft"
                                        : "bg-[#F4F6FB]"
                                }`}>
                                    {isHighlight && (
                                        <span className="inline-block mb-4 px-2.5 py-1 rounded-full bg-[#6B7DB8] text-white text-[10px] font-semibold uppercase tracking-[0.22em]">
                                            The big one
                                        </span>
                                    )}
                                    <p className="font-serif text-5xl text-[#8C9ECB] mb-6">{step.n}</p>
                                    <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center mb-5 shadow-rem-soft">
                                        <Icon size={22} weight="light" color="#4A5A95" />
                                    </div>
                                    <h3 className="font-serif text-2xl text-[#2D2B2A] mb-3">{step.title}</h3>
                                    <p className="text-[#5C5A59] leading-relaxed font-light">{step.body}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.p
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.55 }}
                    className="mt-16 text-center text-base sm:text-lg text-[#5C5A59] font-serif italic max-w-3xl mx-auto leading-relaxed"
                    data-testid="how-bonding-line"
                >
                    {t("how_it_works.bonding_line")}
                </motion.p>
            </div>
        </section>
    );
}
